import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import { CITIES, SECTS, DIET_PRACTICES, PRACTICE_LEVELS, LANGUAGES, GENERAL_PROMPTS, JAIN_PROMPTS, GENDERS, SEEKING_OPTIONS, FEET_OPTIONS, INCHES_OPTIONS, feetInchesToCm, cmToFeetInches } from '@/lib/constants';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ChevronLeft, Plus, X, Camera, Check } from 'lucide-react';
import { compressImage } from '@/lib/image-compress';
import { uploadToStorage, uploadSelfieToStorage } from '@/lib/supabase';
import { toast } from 'sonner';
import TraitsPicker from '@/components/TraitsPicker';
import HobbiesPicker from '@/components/HobbiesPicker';

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState('right');
  const [form, setForm] = useState({
    display_name: '', last_initial: '', dob: '', gender: '', seeking: '',
    city: '', profession: '', education_institution: '', education_degree: '',
    height_feet: 5, height_inches: 6, height_cm_input: 168, height_mode: 'ft',
    sect: '', diet_practice: '', practice_level: '',
    home_language: [], hobbies: '', fav_song: '', fav_show: '',
    personalityTraits: [], hobbiesList: [],
    photos: [], // { url: base64, caption: '' }
    prompts: [{ question: '', answer: '' }, { question: '', answer: '' }, { question: '', answer: '' }],
    selfie: null, // { url: base64 or url, blob, filename }
  });
  const [citySearch, setCitySearch] = useState('');
  const [showCityList, setShowCityList] = useState(false);
  const [promptPicking, setPromptPicking] = useState(null);
  const fileRef = useRef(null);
  const selfieVideoRef = useRef(null);
  const selfieCanvasRef = useRef(null);
  const selfieStreamRef = useRef(null);
  const [selfieReady, setSelfieReady] = useState(false);
  const [selfieError, setSelfieError] = useState('');
  const [selfieCapturing, setSelfieCapturing] = useState(false);
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const u = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const toggleArr = (f, item) => setForm(p => {
    const a = p[f]; return { ...p, [f]: a.includes(item) ? a.filter(i => i !== item) : [...a, item] };
  });

  const stopSelfieCamera = () => {
    if (selfieStreamRef.current) {
      selfieStreamRef.current.getTracks().forEach(track => track.stop());
      selfieStreamRef.current = null;
    }
    if (selfieVideoRef.current) {
      selfieVideoRef.current.srcObject = null;
    }
    setSelfieReady(false);
  };

  useEffect(() => {
    if (step !== 6) {
      stopSelfieCamera();
      setSelfieError('');
      return;
    }

    let cancelled = false;

    const startCamera = async () => {
      setSelfieError('');
      setSelfieReady(false);

      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera access is not supported in this browser.');
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        selfieStreamRef.current = stream;
        if (selfieVideoRef.current) {
          selfieVideoRef.current.srcObject = stream;
          await selfieVideoRef.current.play().catch(() => {});
        }
        setSelfieReady(true);
      } catch (err) {
        console.error('Camera start failed:', err);
        setSelfieError('Camera access is required to take your selfie. Please allow camera permission.');
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stopSelfieCamera();
    };
  }, [step]);

  const captureSelfie = async () => {
    const video = selfieVideoRef.current;
    const canvas = selfieCanvasRef.current;
    if (!video || !canvas) return;

    setSelfieCapturing(true);
    try {
      const width = video.videoWidth || 720;
      const height = video.videoHeight || 960;
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      if (!context) throw new Error('Could not access canvas context.');

      context.drawImage(video, 0, 0, width, height);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(result => {
          if (!result) reject(new Error('Could not capture selfie.'));
          else resolve(result);
        }, 'image/jpeg', 0.92);
      });

      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      setForm(p => ({
        ...p,
        selfie: {
          url: dataUrl,
          blob,
          filename: `selfie_${Date.now()}.jpg`,
        },
      }));
      stopSelfieCamera();
      toast('Selfie captured successfully');
    } catch (err) {
      console.error('Selfie capture failed:', err);
      toast('Could not capture selfie. Please try again.');
    } finally {
      setSelfieCapturing(false);
    }
  };

  const retakeSelfie = () => {
    setForm(p => ({ ...p, selfie: null }));
    setSelfieError('');
    setTimeout(() => {
      if (step === 6) {
        const reStream = async () => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: {
                facingMode: 'user',
                width: { ideal: 1280 },
                height: { ideal: 720 },
              },
              audio: false,
            });
            selfieStreamRef.current = stream;
            if (selfieVideoRef.current) {
              selfieVideoRef.current.srcObject = stream;
              await selfieVideoRef.current.play().catch(() => {});
            }
            setSelfieReady(true);
          } catch (err) {
            console.error('Retake camera start failed:', err);
            setSelfieError('Camera access is required to take your selfie. Please allow camera permission.');
          }
        };
        reStream();
      }
    }, 0);
  };

  const getDobError = () => {
    if (!form.dob) return null;
    const dob = new Date(form.dob);
    const today = new Date();
    if (dob > today) return 'Date of birth cannot be in the future.';
    const age = Math.floor((today - dob) / (365.25 * 24 * 60 * 60 * 1000));
    if (age < 18) return 'You must be at least 18 years old to join Jiv.';
    if (age > 80) return 'Please enter a valid date of birth.';
    return null;
  };

  const canNext = () => {
    switch (step) {
      case 1: return form.display_name.trim() && form.last_initial.trim() && form.dob && !getDobError() && form.gender && form.seeking;
      case 2: return form.city && form.profession.trim() && form.education_degree.trim();
      case 3: return form.sect && form.diet_practice && form.practice_level;
      case 4: return form.home_language.length > 0 && form.hobbies.trim() &&
               form.personalityTraits.length === 5 && form.hobbiesList.length === 5;
      case 5: return form.photos.length >= 3;
      case 6: return form.selfie !== null; // Selfie is mandatory
      case 7: {
        // Validate all prompts are filled and at least 1 is from JAIN_PROMPTS
        const allFilled = form.prompts.every(p => p.question && p.answer.trim());
        const hasJain = form.prompts.some(p => JAIN_PROMPTS.includes(p.question));
        return allFilled && hasJain;
      }
      default: return false;
    }
  };

  const [saving, setSaving] = useState(false);

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (form.photos.length >= 5) break;
      try {
        const { blob, dataUrl } = await compressImage(file, 800, 0.7);
        setForm(p => ({ ...p, photos: [...p.photos, { url: dataUrl, blob, caption: '', filename: file.name }] }));
      } catch (err) {
        console.error('Compression error:', err);
      }
    }
    e.target.value = '';
  };

  const updateCaption = (i, c) => setForm(p => {
    const ph = [...p.photos]; ph[i] = { ...ph[i], caption: c.slice(0, 100) }; return { ...p, photos: ph };
  });

  const removePhoto = (i) => setForm(p => ({ ...p, photos: p.photos.filter((_, idx) => idx !== i) }));

  const selectPrompt = (q) => {
    if (promptPicking === null) return;
    setForm(p => { const ps = [...p.prompts]; ps[promptPicking] = { ...ps[promptPicking], question: q }; return { ...p, prompts: ps }; });
    setPromptPicking(null);
  };

  const handleComplete = async () => {
    const profession = form.profession.trim();
    const degree = form.education_degree.trim();
    if (!profession || !degree) {
      toast('Profession and degree are required.');
      return;
    }

    setSaving(true);
    const heightCm = form.height_mode === 'ft' ? feetInchesToCm(form.height_feet, form.height_inches) : form.height_cm_input;
    const hasJainPrompt = form.prompts.some(p => JAIN_PROMPTS.includes(p.question));
    
    db.createProfile({
      id: user.id, display_name: form.display_name.trim(), last_initial: form.last_initial.trim(),
      dob: form.dob, gender: form.gender, seeking: form.seeking,
      city: form.city, profession,
      education_institution: form.education_institution.trim(), education_degree: degree,
      height_cm: heightCm, sect: form.sect, diet_practice: form.diet_practice, practice_level: form.practice_level,
      home_language: form.home_language,
      hobbies: form.hobbies.trim(), fav_song: form.fav_song.trim(), fav_show: form.fav_show.trim(),
      personality_traits: form.personalityTraits,
      hobbies_list: form.hobbiesList,
      prompt_1_q: form.prompts[0].question, prompt_1_a: form.prompts[0].answer,
      prompt_2_q: form.prompts[1].question, prompt_2_a: form.prompts[1].answer,
      prompt_3_q: form.prompts[2].question, prompt_3_a: form.prompts[2].answer,
      selfie_verified: false, vouch_status: 'none', report_flag: false, referral_code: null, referred_by: null,
      successful_referrals: 0, report_count: 0,
      applied_for_verification: false, applied_at: null, screenshot_warned: false,
      has_jain_prompt: hasJainPrompt,
      created_at: new Date().toISOString(),
    });
    
    // Upload photos (compressed) — try Supabase Storage, fallback to dataUrl
    let uploadFailed = false;
    for (let i = 0; i < form.photos.length; i++) {
      const p = form.photos[i];
      let url = p.url;
      if (p.blob) {
        const storageUrl = await uploadToStorage(user.id, p.blob, p.filename || `photo_${i}.jpg`);
        if (storageUrl) {
          url = storageUrl;
        } else {
          uploadFailed = true;
        }
      }
      db.addPhoto(user.id, url, i, p.caption);
    }
    if (uploadFailed) toast('Some photos could not be uploaded. You can re-upload in Edit Profile.');
    
    // Upload selfie separately — failure should not block profile save
    if (form.selfie) {
      try {
        let selfiePath = null;
        let selfieUrl = form.selfie.url;
        if (form.selfie.blob) {
          const storageSelfie = await uploadSelfieToStorage(user.id, form.selfie.blob, form.selfie.filename || 'selfie.jpg');
          if (storageSelfie?.publicUrl) {
            selfiePath = storageSelfie.path;
            selfieUrl = storageSelfie.publicUrl;
          } else {
            console.warn('[Onboarding] Storage upload returned null or missing publicUrl');
          }
        }
        db.uploadSelfie(user.id, selfiePath, selfieUrl);
      } catch (err) {
        console.error('[Onboarding] Selfie upload error:', err);
        toast('Selfie capture failed. Please try again.');
      }
    }
    
    navigate('/welcome');
    setTimeout(() => refreshProfile(), 50);
    setSaving(false);
  };

  const goNext = () => { if (step < 7) { setDir('right'); setStep(step + 1); } else handleComplete(); };
  const goBack = () => { if (step > 1) { setDir('left'); setStep(step - 1); } };

  const titles = ['The basics', 'Location & work', 'Jain identity', 'Personality', 'Photos', 'Selfie', 'Prompts'];
  const filteredCities = CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase()));
  const selectedQs = form.prompts.map(p => p.question).filter(Boolean);
  const animClass = dir === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left';

  return (
    <div className="h-[100dvh] flex flex-col bg-[#FAF8F5]" data-testid="onboarding">
      <div className="px-4 pt-4 pb-2 shrink-0">
        <Progress value={(step / 7) * 100} className="h-1 transition-all duration-300" />
      </div>
      <div className="flex items-center gap-3 px-4 pb-3 shrink-0">
        {step > 1 && <button onClick={goBack} className="text-foreground" data-testid="onboarding-back"><ChevronLeft className="h-5 w-5" /></button>}
        <h1 className="text-[22px] font-semibold text-[#1A1814]" data-testid="onboarding-title">{titles[step - 1]}</h1>
        <span className="ml-auto text-xs text-[#A8A5A0]">{step}/7</span>
      </div>

      <div key={step} className={`flex-1 overflow-auto px-4 pb-4 hide-scrollbar ${animClass}`}>
        {step === 1 && (
          <div className="space-y-4">
            <Field label="First name"><Input value={form.display_name} onChange={e => u('display_name', e.target.value)} placeholder="Your first name" className="h-12 rounded-xl bg-white" data-testid="input-name" /></Field>
            <Field label="Last name"><Input value={form.last_initial} onChange={e => u('last_initial', e.target.value)} placeholder="e.g. Sharma" className="h-12 rounded-xl bg-white" data-testid="input-last-name" /></Field>
            <Field label="Date of birth">
              <Input type="date" value={form.dob} onChange={e => u('dob', e.target.value)} className="h-12 rounded-xl bg-white" data-testid="input-dob" />
              {form.dob && getDobError() && <p className="text-destructive text-xs mt-1">{getDobError()}</p>}
            </Field>
            <Field label="I am a">
              <div className="flex flex-wrap gap-2">
                {GENDERS.map(g => <Chip key={g} selected={form.gender===g} onClick={() => u('gender',g)} testId={`gender-${g.toLowerCase()}`}>{g}</Chip>)}
              </div>
            </Field>
            <Field label="Interested in">
              <div className="flex flex-wrap gap-2">
                {SEEKING_OPTIONS.map(s => <Chip key={s} selected={form.seeking===s} onClick={() => u('seeking',s)} testId={`seeking-${s.toLowerCase()}`}>{s}</Chip>)}
              </div>
            </Field>
            {/* Height — mandatory */}
            <Field label="Height">
              <div className="flex items-center gap-2 mb-2">
                <button onClick={() => u('height_mode', 'ft')} className={`text-xs px-3 py-1 rounded-full ${form.height_mode==='ft' ? 'bg-[#C857A8] text-white' : 'bg-[#F0EDE9] text-[#1A1814]'}`}>ft/in</button>
                <button onClick={() => u('height_mode', 'cm')} className={`text-xs px-3 py-1 rounded-full ${form.height_mode==='cm' ? 'bg-[#C857A8] text-white' : 'bg-[#F0EDE9] text-[#1A1814]'}`}>Switch to cm</button>
              </div>
              {form.height_mode === 'ft' ? (
                <div className="flex gap-2">
                  <select value={form.height_feet} onChange={e => u('height_feet', +e.target.value)} className="h-12 px-3 rounded-xl bg-white border border-border text-sm flex-1" data-testid="height-feet">
                    {FEET_OPTIONS.map(f => <option key={f} value={f}>{f} ft</option>)}
                  </select>
                  <select value={form.height_inches} onChange={e => u('height_inches', +e.target.value)} className="h-12 px-3 rounded-xl bg-white border border-border text-sm flex-1" data-testid="height-inches">
                    {INCHES_OPTIONS.map(i => <option key={i} value={i}>{i} in</option>)}
                  </select>
                </div>
              ) : (
                <Input type="number" value={form.height_cm_input} onChange={e => u('height_cm_input', +e.target.value)} min={120} max={220} className="h-12 rounded-xl bg-white" placeholder="Height in cm" data-testid="height-cm" />
              )}
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="relative">
              <Field label="City">
                <Input value={form.city || citySearch} onChange={e => { setCitySearch(e.target.value); u('city',''); setShowCityList(true); }} onFocus={() => setShowCityList(true)} placeholder="Search your city..." className="h-12 rounded-xl bg-white" data-testid="input-city" />
              </Field>
              {showCityList && <div className="absolute z-20 w-full mt-1 bg-white border border-border rounded-xl max-h-48 overflow-auto shadow-sm">
                {filteredCities.map(c => <button key={c} onClick={() => { u('city',c); setCitySearch(c); setShowCityList(false); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAF8F5] text-[#1A1814]" data-testid={`city-${c.toLowerCase()}`}>{c}</button>)}
              </div>}
            </div>
            <Field label="Profession or field of study">
              <Input value={form.profession} onChange={e => u('profession', e.target.value.slice(0,60))} placeholder="What do you do?" className="h-12 rounded-xl bg-white" data-testid="input-profession" />
              <p className="text-xs text-[#A8A5A0] mt-1">Student? Just write that.</p>
            </Field>
            <Field label="College or university" optional>
              <Input value={form.education_institution} onChange={e => u('education_institution', e.target.value)} placeholder="College or university" className="h-12 rounded-xl bg-white" data-testid="input-edu-inst" />
            </Field>
            <Field label="Degree">
              <Input value={form.education_degree} onChange={e => u('education_degree', e.target.value)} placeholder="e.g. B.Com, MBA, B.Tech" className="h-12 rounded-xl bg-white" data-testid="input-edu-degree" />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <Field label="Sect">
              <div className="space-y-2">{SECTS.map(s => <SelectCard key={s.value} selected={form.sect===s.value} onClick={() => u('sect',s.value)} title={s.value} desc={s.description} testId={`sect-${s.value.toLowerCase().replace(/\s/g,'-')}`} />)}</div>
            </Field>
            <Field label="Diet practice">
              <div className="space-y-2">{DIET_PRACTICES.map(d => <SelectCard key={d} selected={form.diet_practice===d} onClick={() => u('diet_practice',d)} title={d} testId={`diet-${d.slice(0,20).toLowerCase().replace(/\s/g,'-')}`} />)}</div>
            </Field>
            <Field label="Practice level">
              <div className="space-y-2">{PRACTICE_LEVELS.map(p => <SelectCard key={p.value} selected={form.practice_level===p.value} onClick={() => u('practice_level',p.value)} title={p.value} desc={p.description} testId={`practice-${p.value.toLowerCase()}`} />)}</div>
            </Field>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <Field label="Languages spoken at home">
              <div className="flex flex-wrap gap-2">{LANGUAGES.map(l => <Chip key={l} selected={form.home_language.includes(l)} onClick={() => toggleArr('home_language',l)} testId={`lang-${l.toLowerCase()}`}>{l}</Chip>)}</div>
            </Field>
            <Field label="What do you love doing?">
              <Textarea value={form.hobbies} onChange={e => u('hobbies', e.target.value.slice(0,200))} placeholder="Travel, cooking, cricket, reading, hiking..." className="min-h-[80px] rounded-xl bg-white resize-none" data-testid="input-hobbies" />
              <p className="text-right text-[10px] text-[#A8A5A0] mt-1">{form.hobbies.length}/200</p>
            </Field>
            <Field label="Favourite song"><Input value={form.fav_song} onChange={e => u('fav_song', e.target.value.slice(0,60))} placeholder="Song that defines you" className="h-12 rounded-xl bg-white" data-testid="input-fav-song" /></Field>
            <Field label="Favourite show"><Input value={form.fav_show} onChange={e => u('fav_show', e.target.value.slice(0,60))} placeholder="Your comfort show" className="h-12 rounded-xl bg-white" data-testid="input-fav-show" /></Field>
            <div>
              <TraitsPicker
                selected={form.personalityTraits}
                onChange={v => u('personalityTraits', v)}
                title="Your personality"
                subtitle="Pick 5 that feel most like you"
              />
              {form.personalityTraits.length > 0 && form.personalityTraits.length < 5 && (
                <p className="text-xs text-[#7A7672] mt-2">Pick exactly 5 traits to continue</p>
              )}
            </div>
            <div>
              <HobbiesPicker
                selected={form.hobbiesList}
                onChange={v => u('hobbiesList', v)}
                title="What you're into"
                subtitle="Pick 5 across any category"
              />
            </div>
            {(form.personalityTraits.length > 0 || form.hobbiesList.length > 0) &&
             (form.personalityTraits.length !== 5 || form.hobbiesList.length !== 5) && (
              <p className="text-xs text-[#A8A5A0] text-center">Select 5 personality traits and 5 hobbies to continue</p>
            )}
          </div>
        )}

        {step === 5 && (
          <div>
            <p className="text-sm text-[#7A7672] mb-2">Add at least 3 photos to continue. Your first photo is your cover.</p>
            <p className="text-xs text-[#C857A8] bg-[#FBE7F3] rounded-lg px-3 py-2 mb-4">💡 Tip: Make your cover photo a recent, clear photo of yourself (face/upper body preferred).</p>
            <div className="grid grid-cols-3 gap-2">
              {form.photos.map((p, i) => (
                <div key={i}>
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-[#F0EDE9]">
                    <img src={p.url} className="w-full h-full object-cover no-screenshot" alt={`Photo ${i+1}`} onContextMenu={(e) => e.preventDefault()} draggable={false} />
                    <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center"><X className="h-3 w-3 text-white" /></button>
                    {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] bg-[#C857A8] text-white px-2 py-0.5 rounded-full font-medium">Cover</span>}
                  </div>
                  <Input value={p.caption} onChange={e => updateCaption(i, e.target.value)} placeholder="Add a caption" className="mt-1 h-8 text-xs rounded-lg bg-white" maxLength={100} data-testid={`photo-caption-${i}`} />
                </div>
              ))}
              {form.photos.length < 5 && (
                <button onClick={() => fileRef.current?.click()} className="aspect-[3/4] rounded-xl border-2 border-dashed border-[rgba(0,0,0,0.10)] flex flex-col items-center justify-center gap-1 hover:border-[#C857A8]/50 transition-colors" data-testid="add-photo-btn">
                  <Camera className="h-6 w-6 text-[#A8A5A0]" /><span className="text-[10px] text-[#A8A5A0]">Add</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} />
            <p className="text-xs text-[#A8A5A0] mt-3">{form.photos.length}/5 photos · Minimum 3 required</p>
          </div>
        )}

        {step === 6 && (
          <div>
            <p className="text-sm text-[#7A7672] mb-4">Take a live selfie with your camera. This is required.</p>
            <div className="flex flex-col items-center gap-5">
              {form.selfie ? (
                <>
                  <div className="relative w-48 h-64 rounded-2xl overflow-hidden bg-[#F0EDE9] shadow-sm">
                    <img src={form.selfie.url} className="w-full h-full object-cover no-screenshot" alt="Selfie" data-testid="selfie-preview" onContextMenu={(e) => e.preventDefault()} draggable={false} />
                    <button onClick={() => { setForm(p => ({ ...p, selfie: null })); retakeSelfie(); }} className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors">
                      <X className="h-4 w-4 text-white" />
                    </button>
                  </div>
                  <Button onClick={retakeSelfie} variant="outline" className="rounded-full px-5" data-testid="retake-selfie-btn">
                    Retake selfie
                  </Button>
                  <p className="text-xs text-[#A8A5A0] text-center">This selfie is used for approval and is not publicly shown.</p>
                </>
              ) : (
                <>
                  <div className="w-full max-w-sm">
                    <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-black border border-[rgba(0,0,0,0.08)] shadow-sm">
                      <video ref={selfieVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="selfie-video" />
                      {!selfieReady && !selfieError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <p className="text-sm text-white">Starting camera...</p>
                        </div>
                      )}
                      {selfieError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/70 px-4 text-center">
                          <p className="text-sm text-white">{selfieError}</p>
                        </div>
                      )}
                    </div>
                    <canvas ref={selfieCanvasRef} className="hidden" aria-hidden="true" />
                  </div>
                  <Button
                    onClick={captureSelfie}
                    disabled={!selfieReady || selfieCapturing || !!selfieError}
                    className="px-6 py-2.5 rounded-full bg-[#C857A8] text-white text-sm font-medium hover:bg-[#B646A0] transition-colors"
                    data-testid="capture-selfie-btn"
                  >
                    {selfieCapturing ? 'Capturing...' : 'Capture selfie'}
                  </Button>
                  <p className="text-xs text-[#A8A5A0] text-center">Look into the camera and tap Capture selfie.</p>
                </>
              )}
            </div>
          </div>
        )}

        {step === 7 && (
          <div>
            <p className="text-sm text-[#7A7672] mb-4">Pick 3 prompts and write your answers (150 chars each).</p>
            <div className="space-y-3">
              {form.prompts.map((p, i) => (
                <div key={i} className="p-4 rounded-[14px] bg-[#F7F5F2]">
                  {p.question ? (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-medium flex-1">{p.question}</p>
                        <button onClick={() => { const ps = [...form.prompts]; ps[i] = { question:'', answer:'' }; u('prompts', ps); }} className="text-[#A8A5A0]"><X className="h-4 w-4" /></button>
                      </div>
                      <Textarea value={p.answer} onChange={e => { const ps = [...form.prompts]; ps[i] = { ...ps[i], answer: e.target.value.slice(0,150) }; u('prompts', ps); }}
                        placeholder="Write your answer..." className="mt-2 min-h-[60px] resize-none rounded-lg text-sm bg-white" data-testid={`prompt-answer-${i+1}`} />
                      <p className="text-right text-[10px] text-[#A8A5A0] mt-1">{p.answer.length}/150</p>
                    </>
                  ) : (
                    <button onClick={() => setPromptPicking(i)} className="w-full text-left text-sm text-[#A8A5A0] hover:text-[#1A1814]" data-testid={`pick-prompt-${i+1}`}>
                      <Plus className="h-4 w-4 inline mr-1.5" />Select prompt {i+1}
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {!form.prompts.some(p => JAIN_PROMPTS.includes(p.question)) && form.prompts.some(p => p.question) && (
              <div className="mt-4 p-3 rounded-xl bg-[#FBE7F3] border border-[#F0D0E8]">
                <p className="text-xs text-[#C857A8]">⚠️ You must select at least one Jain-specific prompt</p>
              </div>
            )}
            
            {promptPicking !== null && (
              <div className="fixed inset-0 z-50 bg-[#FAF8F5] flex flex-col" data-testid="prompt-picker">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <h3 className="font-semibold text-[#1A1814]">Pick a prompt</h3>
                  <button onClick={() => setPromptPicking(null)}><X className="h-5 w-5" /></button>
                </div>
                <div className="flex-1 overflow-auto p-4 space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.08em] text-[#C857A8] font-bold mb-2">Required — pick at least 1</p>
                    {JAIN_PROMPTS.filter(p => !selectedQs.includes(p)).map(p => (
                      <button key={p} onClick={() => selectPrompt(p)} className="block w-full text-left py-2.5 text-sm text-[#1A1814] border-b border-[rgba(0,0,0,0.05)] hover:text-[#C857A8]" data-testid={`jain-prompt-${JAIN_PROMPTS.indexOf(p)}`}>{p}</button>
                    ))}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.08em] text-[#A8A5A0] font-medium mb-2">General</p>
                    {GENERAL_PROMPTS.filter(p => !selectedQs.includes(p)).map(p => (
                      <button key={p} onClick={() => selectPrompt(p)} className="block w-full text-left py-2.5 text-sm text-[#1A1814] border-b border-[rgba(0,0,0,0.05)] hover:text-[#C857A8]">{p}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-4 pb-6 pt-2 shrink-0">
        <Button onClick={goNext} disabled={!canNext()}
          className={`w-full h-[52px] text-[15px] font-semibold rounded-[26px] ${canNext() ? 'bg-[#C857A8] hover:bg-saffron-mid' : 'btn-disabled-saffron'}`}
          data-testid="onboarding-next">
          {step === 7 ? (saving ? 'Saving...' : 'Complete profile') : 'Continue'}
        </Button>
      </div>
    </div>
  );
}

// Reusable sub-components
function Field({ label, optional, children }) {
  return (
    <div>
      <label className="text-[12px] uppercase tracking-[0.06em] text-[#A8A5A0] font-medium mb-1.5 block">
        {label}{optional && <span className="normal-case tracking-normal text-[#A8A5A0]"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

function Chip({ selected, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-4 py-2 rounded-full border text-sm transition-colors ${selected ? 'border-[#C857A8] bg-[#C857A8] text-white' : 'border-[rgba(0,0,0,0.10)] bg-white text-[#1A1814]'}`}>
      {children}
    </button>
  );
}

function SelectCard({ selected, onClick, title, desc, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`w-full text-left p-3 rounded-xl border transition-colors ${selected ? 'border-[#C857A8] bg-saffron-light' : 'border-[rgba(0,0,0,0.10)] bg-white'}`}>
      <span className="text-sm font-medium text-[#1A1814]">{title}</span>
      {desc && <p className="text-xs text-[#A8A5A0] mt-0.5">{desc}</p>}
      {selected && <Check className="inline h-4 w-4 text-[#C857A8] ml-1" />}
    </button>
  );
}
